import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { generatePath } from 'react-router-dom';
import { BreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type Breadcrumb = Pick<BreadcrumbItem, 'name' | 'url' | 'noLink' | 'dataTestId'>;

type DirectoryBreadcrumbsBaseProps = {
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
	dealId: string;
	noLink?: boolean;
	dataTestId?: string;
};

type DirectoryBreadcrumbsPersonProps = DirectoryBreadcrumbsBaseProps & {
	personId?: string;
	personDisplayName?: string;
	subBreadcrumbs?: Breadcrumb[];
};

const makePersonDetailsBreadcrumb = ({
	tCapManage,
	dealId,
	personId,
	personDisplayName,
	subBreadcrumbs = [],
}: DirectoryBreadcrumbsPersonProps): Breadcrumb[] => {
	return [
		{
			name: tCapManage('header.title_create'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}`, {
				dealId,
				personId: '',
			}),
			noLink: true,
		},
		...(personId
			? [
					{
						name: personDisplayName ?? '',
						url: generatePath(
							`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
							{
								dealId,
								personId,
							}
						),
					},
					...subBreadcrumbs,
				]
			: []),
	];
};

export const DIRECTORY_BREADCRUMBS: {
	[key in PEOPLE_ROUTES]: (
		props: DirectoryBreadcrumbsBaseProps | DirectoryBreadcrumbsPersonProps
	) => Breadcrumb[];
} = {
	[PEOPLE_ROUTES.INITIAL]: ({ dealId, tCapManage }: DirectoryBreadcrumbsBaseProps) => [
		{
			name: tCapManage('header.title_create'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}`, {
				dealId,
				personId: '',
			}),
		},
	],
	[PEOPLE_ROUTES.PERSON_DETAILS]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.basic_details.title'),
					url: '',
					noLink: true,
				},
			],
		}),
	[PEOPLE_ROUTES.PERSON_HISTORY]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.basic_details.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
				},
				{
					name: props.tCapManage('people.details.history', {
						defaultValue: 'History',
					}),
					url: '',
					noLink: true,
				},
			],
		}),
	[PEOPLE_ROUTES.SECURITIES]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.securities.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.SECURITIES}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
					dataTestId: props.dataTestId,
				},
			],
		}),
	[PEOPLE_ROUTES.TRANSACTIONS]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.transactions.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.TRANSACTIONS}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
				},
			],
		}),
	[PEOPLE_ROUTES.SECURITY_DETAILS]: (props: DirectoryBreadcrumbsPersonProps) => [
		...DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.SECURITIES](props),
		{
			name: props.tCapManage('securities.details.title'),
			url: '',
			noLink: true,
		},
	],
	[PEOPLE_ROUTES.SECURITY_RANKING]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.securities.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.SECURITIES}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
					dataTestId: props.dataTestId,
				},
				{
					name: props.tCapManage('tabs.security_ranking.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.SECURITY_RANKING}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
				},
			],
		}),
	[PEOPLE_ROUTES.TRANSACTION_DETAILS]: (props: DirectoryBreadcrumbsPersonProps) => [
		...DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.TRANSACTIONS](props),
		{
			name: props.tCapManage('transactions.details.title'),
			url: '',
			noLink: true,
		},
	],
	[PEOPLE_ROUTES.HOLDINGS]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.ownership.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.HOLDINGS}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
				},
			],
		}),
	[PEOPLE_ROUTES.HOLDING_DETAILS]: (props: DirectoryBreadcrumbsPersonProps) => [
		...DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.HOLDINGS](props),
		{
			name: props.tCapManage('holdings.details.title'),
			url: '',
			noLink: true,
		},
	],
	[PEOPLE_ROUTES.OWNERSHIP]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.holdings.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.OWNERSHIP}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
					noLink: props.noLink,
				},
			],
		}),
	[PEOPLE_ROUTES.DOCUMENTS]: (props: DirectoryBreadcrumbsPersonProps) =>
		makePersonDetailsBreadcrumb({
			...props,
			subBreadcrumbs: [
				{
					name: props.tCapManage('tabs.documents.title'),
					url: generatePath(
						`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.DOCUMENTS}`,
						{
							dealId: props.dealId,
							personId: props.personId ?? '',
						}
					),
				},
			],
		}),
};
